import { useState, useEffect, useMemo } from 'react';
import { Search, X, Minus, Plus, LayoutGrid, Package } from 'lucide-react';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { spinnerRing } from '../utils/bookingUtils';
import { formatMoney, formatAmount, roundMoney } from '../utils/currencyUtil';
import {
  subscribePosProducts, subscribePosCategories, subscribePosOrders, placePosOrder,
  subscribePosCategoryIcons, posIconIsIconify, posIconifyUrl,
  posRemainingStock, posStockLevel, posVatBreakdown,
  sortPosProducts, tallyPosUnitsSold, posProductPageSize,
  subscribePosPaymentMethods
} from '../utils/posUtil';
import {
  POS_ALL_CATEGORIES,
  POS_SEARCH_MIN_CHARS,
  POS_VAT_MODE_INCLUSIVE,
  POS_VAT_MODE_EXCLUSIVE,
  POS_DEFAULT_PAYMENT_METHOD,
  POS_CASH_METHOD,
  POS_SORT_BY_SOLD,
  POS_SORT_BY_CUSTOM,
  POS_CUSTOM_SORT_LISTS,
  POS_SORT_PREFERENCE_SCREENS,
  POS_DELIVERY_FEE_MAX,
} from '../constants/posConst';
import { posCardGridAttrs } from '../utils/posCardSizeUtil';
import useCustomSort from '../hooks/useCustomSort';
import useSavedSort from '../hooks/useSavedSort';
import useDragReorder from '../hooks/useDragReorder';
import PosSortBar from './PosSortBar';
import PosDeliveryFeeField from './PosDeliveryFeeField';
import PosPager from './PosPager';
import PosReceipt from './PosReceipt';
import PosCustomizationPicker from './PosCustomizationPicker';
import PosSelectedOptions from './PosSelectedOptions';
import {
  resolvePosSelections, posCartLineKey, posHasCustomizations,
} from '../utils/posCustomizationUtil';
import Modal from './Modal';
import '../css/Pos.css';

/**
 * Product dashboard — the till.
 *
 * Laid out to the reference design: category cards with item counts across
 * the top, one search field under them, an image-led product grid, and the
 * order kept in a rail down the side with its own totals block.
 *
 * Tap a product to open the side panel, choose how many, add it. "Place
 * order" shows the whole thing as a receipt to confirm or cancel; confirming
 * is what decrements stock and writes the order (see placePosOrder).
 *
 * @returns {JSX.Element}
 */
function PosDashboard() {
  const { config, clientId } = useConfig();
  // Recorded as the cashier on the order and on its audit entry.
  //
  // `can` also decides whether this screen can TAKE money or only show what is
  // for sale: a role may grant "view products" without "process orders", which
  // is the shape of a stock-checker or a supervisor walking the floor.
  const { staffEmail, can } = usePosSession();
  const canProcessOrders = can('processOrders');
  // Saving a Custom order changes what every till here lists first, so it is
  // held to the stock/settings rights rather than open to any cashier.
  const canArrange = can('addStock') || can('manageConfig');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  // name -> icon, set in Inventory. Absent leaves the generic mark below.
  const [categoryIcons, setCategoryIcons] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(POS_ALL_CATEGORIES);
  const [search, setSearch] = useState('');
  // Opens on the sort this client last picked here, and remembers changes.
  const {
    sortKey, sortDir, loaded: sortLoaded, changeSort, toggleDirection,
  } = useSavedSort({ clientId, screen: POS_SORT_PREFERENCE_SCREENS.PRODUCTS, actor: staffEmail });
  // Units sold per product, for the "Items sold" order. Null until the orders
  // have been read, which only happens if that order is actually chosen —
  // see the effect below.
  const [unitsSold, setUnitsSold] = useState(null);
  // The club's own payment methods, live — Config -> Payments. Falls back to
  // the built-in list for a client that has never edited them.
  const [payMethods, setPayMethods] = useState([]);
  // 1-based. Reset whenever the list underneath changes — see the effect below.
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [panelQty, setPanelQty] = useState(1);
  // The customization answers chosen in the panel, for the product it shows.
  const [panelPicks, setPanelPicks] = useState({});
  // { [lineKey]: { productId, quantity, picks } } — the order being built.
  // Keyed by LINE rather than by product: the same milk tea at 50% and at
  // 100% sugar is two lines to make. A product with no options has the
  // product id as its key, so it still collapses into one line.
  const [cart, setCart] = useState({});
  const [charging, setCharging] = useState(false);
  const [confirming, setConfirming] = useState(false);
  // Cash tendered, as typed. Kept a string so the field can be empty — a
  // card or e-wallet sale leaves it blank and the receipt omits CASH/CHANGE.
  const [cashReceived, setCashReceived] = useState('');
  // How the customer is paying. Opens on cash, which is most counter sales.
  const [paymentMethod, setPaymentMethod] = useState(POS_DEFAULT_PAYMENT_METHOD);
  // "With delivery fee" on the confirmation, and the amount as typed.
  const [withDelivery, setWithDelivery] = useState(false);
  const [deliveryFee, setDeliveryFee] = useState('');
  // The recorded order, shown as a receipt once the sale goes through. This
  // replaces the old "Order recorded" alert: a receipt is what staff and
  // customer actually need to see at that moment.
  const [placed, setPlaced] = useState(null);
  const [modal, setModal] = useState(null);

  useEffect(() => {
    const stopProducts = subscribePosProducts(clientId, (list) => { setProducts(list); setLoading(false); });
    const stopCategories = subscribePosCategories(clientId, setCategories);
    const stopCategoryIcons = subscribePosCategoryIcons(clientId, setCategoryIcons);
    const stopPayments = subscribePosPaymentMethods(clientId, setPayMethods);
    return () => { stopProducts(); stopCategories(); stopCategoryIcons(); stopPayments(); };
  }, [clientId]);

  // Orders are read ONLY while the grid is ordered by what has sold.
  //
  // Deliberately lazy. This is the screen every till sits on all day, and the
  // orders node is the heaviest one a client has — subscribing to it on mount
  // would put the last 300 orders, with every line item in them, on the wire
  // for every session whether or not anyone ever opens the sort control. The
  // listener is opened when the field is chosen and closed when it is left.
  useEffect(() => {
    if (sortKey !== POS_SORT_BY_SOLD) return undefined;
    const stop = subscribePosOrders(clientId, (orders) => setUnitsSold(tallyPosUnitsSold(orders)));
    return stop;
  }, [clientId, sortKey]);

  const isCustomSort = sortKey === POS_SORT_BY_CUSTOM;
  const customSort = useCustomSort({
    clientId,
    listKey: POS_CUSTOM_SORT_LISTS.SHARED,
    active: isCustomSort,
    products,
    actor: staffEmail,
  });
  const arranging = isCustomSort && canArrange;
  const { draggingId, getDragProps } = useDragReorder({ enabled: arranging, onMove: customSort.move });

  /**
   * Saves the dragged arrangement, reporting a failure in the modal.
   * @returns {Promise<void>}
   */
  const handleSaveCustomSort = async () => {
    try {
      await customSort.save();
    } catch (error) {
      setModal({ title: 'Could not save the order', message: error.message, type: 'error' });
    }
  };

  /**
   * A category's chosen icon, or the generic mark when it has none.
   *
   * An Iconify id is drawn as a CSS mask rather than an <img>: that way the
   * icon takes currentColor and inverts with the active card, and nothing
   * fetched from a third party is injected into this app as markup.
   *
   * @param {string} name - the category
   * @returns {JSX.Element}
   */
  const categoryIcon = (name) => {
    const icon = categoryIcons?.[name];
    if (!icon) return <Package size={18} />;
    if (!posIconIsIconify(icon)) return <span aria-hidden="true">{icon}</span>;
    return (
      <span
        className="pos-icon-mask"
        style={{ WebkitMaskImage: `url(${posIconifyUrl(icon)})`, maskImage: `url(${posIconifyUrl(icon)})` }}
        aria-hidden="true"
      />
    );
  };

  // Counts for the category cards. Taken from the WHOLE product list, not the
  // filtered one: a card saying "20 items" that changes as you type would be
  // reporting your search rather than the category.
  const countsByCategory = useMemo(() => {
    const counts = new Map();
    products.forEach((p) => {
      const key = p.category || '';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [products]);

  const visibleProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = products.filter((product) => {
      if (activeCategory !== POS_ALL_CATEGORIES && product.category !== activeCategory) return false;
      if (term.length < POS_SEARCH_MIN_CHARS) return true;
      return `${product.name || ''} ${product.description || ''}`.toLowerCase().includes(term);
    });
    // Sorted AFTER filtering, on the short list rather than the whole
    // catalogue — and sortPosProducts returns a new array, so `products` in
    // state is never reordered under React.
    return sortPosProducts(matching, {
      key: sortKey, direction: sortDir, unitsSold, customOrder: customSort.order,
    });
  }, [products, activeCategory, search, sortKey, sortDir, unitsSold, customSort.order]);

  // 0 means show everything, which is the default and what this screen did
  // before the setting existed. Config -> Product Settings sets it per client.
  const pageSize = posProductPageSize(config);

  // Back to the first page whenever the list underneath changes. Without this,
  // narrowing a 60-product catalogue to 4 while on page 3 shows an empty grid
  // with no hint that the products are one click away.
  useEffect(() => { setPage(1); }, [activeCategory, search, sortKey, sortDir, pageSize]);

  // Clamped as well as reset, because the list can also shrink on its own —
  // another till selling the last of something removes it from the grid, and
  // the page you were on can stop existing without you touching a control.
  const pageCount = pageSize > 0 ? Math.max(1, Math.ceil(visibleProducts.length / pageSize)) : 1;
  const safePage = Math.min(page, pageCount);

  const pagedProducts = useMemo(() => (
    pageSize > 0
      ? visibleProducts.slice((safePage - 1) * pageSize, safePage * pageSize)
      : visibleProducts
  ), [visibleProducts, pageSize, safePage]);

  // Rebuilt from the live product list rather than stored: a price or name
  // edited in Inventory mid-order must not leave the rail quoting the old one.
  // The options are re-priced the same way, from the answers kept on the line.
  const cartLines = useMemo(() => Object.entries(cart)
    .map(([key, line]) => {
      const product = products.find(p => p.id === line.productId);
      if (!product) return null;
      const { selected, extra } = resolvePosSelections(product, line.picks);
      return {
        key,
        product,
        quantity: line.quantity,
        options: selected,
        unitPrice: roundMoney((Number(product.price) || 0) + extra),
      };
    })
    .filter(Boolean), [cart, products]);

  // Units of each product the order holds, across all of its lines. This, not
  // the cart itself, is what stock is checked against — two differently
  // customized lines still draw on the one shelf.
  const claimed = useMemo(() => {
    const totals = {};
    Object.values(cart).forEach(({ productId, quantity }) => {
      totals[productId] = (totals[productId] || 0) + quantity;
    });
    return totals;
  }, [cart]);

  // What the ITEMS came to, before any tax.
  const cartSubtotal = cartLines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0);

  // The client's VAT settings, applied. On "No VAT" this is the subtotal
  // unchanged with `applies: false`, so the rail below renders exactly as it
  // did before VAT existed.
  const vat = posVatBreakdown(cartSubtotal, config);
  // WHAT THE CUSTOMER PAYS — the subtotal on No VAT and VAT Inclusive, the
  // subtotal plus tax on VAT Exclusive. Everything that quotes a price to the
  // customer reads this rather than the subtotal: the Place order button, the
  // change arithmetic and the short-payment warning.
  const cartTotal = vat.total;

  // The delivery fee, once ticked and typed. Charged on top of the goods and
  // outside VAT — it is a service the club passes on, not a taxed line.
  const parsedFee = Number(deliveryFee);
  const feeValid = !withDelivery || deliveryFee === ''
    || (Number.isFinite(parsedFee) && parsedFee >= 0 && parsedFee <= POS_DELIVERY_FEE_MAX);
  const appliedFee = withDelivery && feeValid ? roundMoney(Math.max(0, parsedFee || 0)) : 0;
  // What the customer pays at the confirm step: goods (with VAT) plus delivery.
  const chargeTotal = roundMoney(cartTotal + appliedFee);

  const draftOrder = {
    items: cartLines.map(({ product, quantity, options, unitPrice }) => ({
      productId: product.id,
      name: product.name,
      price: unitPrice,
      quantity,
      lineTotal: roundMoney(unitPrice * quantity),
      options: options.length > 0 ? options : null,
      // Same as placePosOrder stores, so the confirmation can show the base
      // the options were added to.
      basePrice: options.length > 0 ? (Number(product.price) || 0) : null,
    })),
    subtotal: vat.subtotal,
    // Same shape placePosOrder stores, so the receipt shown before charging and
    // the one shown after are rendered from identical fields rather than from
    // two nearly-identical ones.
    vat: vat.applies ? vat.vat : null,
    vatNet: vat.applies ? vat.net : null,
    vatRate: vat.applies ? vat.rate : null,
    vatMode: vat.applies ? vat.mode : null,
    deliveryFee: appliedFee > 0 ? appliedFee : null,
    total: chargeTotal,
  };

  // Floored at 0 so a short payment never shows negative change, and rounded
  // for the same reason placePosOrder rounds it — this value is handed to the
  // receipt, and the confirmation slip must show the figure that will be
  // recorded, not a binary-floating-point neighbour of it.
  const cashChange = cashReceived === '' ? null : roundMoney(Math.max(0, Number(cashReceived) - chargeTotal));

  // THE SELECTION FOLLOWS THE LIST. A club can remove a method — including
  // the one this screen opens on — and without this the till would sit with
  // nothing highlighted and place the order against a method the club no
  // longer offers.
  useEffect(() => {
    if (payMethods.length === 0) return;
    if (payMethods.some(m => m.key === paymentMethod)) return;
    const fallback = payMethods.find(m => m.key === POS_DEFAULT_PAYMENT_METHOD) || payMethods[0];
    setPaymentMethod(fallback.key);
    // Cash is the only method that tenders an amount, so moving off it clears
    // whatever was typed.
    if (fallback.key !== POS_CASH_METHOD) setCashReceived('');
  }, [payMethods, paymentMethod]);

  const payingCash = paymentMethod === POS_CASH_METHOD;

  /**
   * Switches the payment method, dropping any cash figure when moving off
   * cash — a number left behind from a change of mind would otherwise be
   * quoted on the confirmation slip for a sale that never took it.
   *
   * @param {string} key - one of this client's payment method keys
   * @returns {void}
   */
  const choosePaymentMethod = (key) => {
    setPaymentMethod(key);
    if (key !== POS_CASH_METHOD) setCashReceived('');
  };

  /**
   * Closes the confirm step and clears what was entered on it, so reopening
   * starts clean rather than resuming a half-finished payment.
   *
   * @returns {void}
   */
  const closeCharging = () => {
    setCharging(false);
    setCashReceived('');
    setPaymentMethod(POS_DEFAULT_PAYMENT_METHOD);
    setWithDelivery(false);
    setDeliveryFee('');
  };

  // What the panel may still add for the product it is showing. Recomputed
  // every render so it follows both the live stock figure and the order as it
  // is built up.
  const panelMax = selected ? posRemainingStock(selected, claimed) : 0;
  const panelInCart = selected ? (claimed[selected.id] || 0) : 0;
  // The answers so far, priced — what one item costs with them, and which
  // required questions are still open (Add to order waits for those).
  const panelChoice = selected ? resolvePosSelections(selected, panelPicks) : { selected: [], extra: 0, missing: [] };
  const panelUnitPrice = selected ? roundMoney((Number(selected.price) || 0) + panelChoice.extra) : 0;

  /**
   * Holds a typed or stepped quantity inside 1..panelMax.
   *
   * Returns 0 when there is nothing left to add, which is the only case where
   * a quantity below 1 is meaningful — the Add button is disabled there, so
   * the field reads 0 rather than offering a 1 that cannot be honoured.
   *
   * @param {number|string} value - the requested quantity
   * @returns {number} a quantity the order can actually take
   */
  const clampPanelQty = (value) => {
    if (panelMax <= 0) return 0;
    const requested = Math.floor(Number(value)) || 0;
    return Math.min(panelMax, Math.max(1, requested));
  };


  const openProduct = (product) => {
    setSelected(product);
    // Every product starts with nothing picked — carrying the last drink's
    // sugar level over to the next one is how orders come out wrong.
    setPanelPicks({});
    // Opens at 1, or at 0 when the product is spoken for — clamped against
    // that product's own remaining stock, not the one previously open.
    setPanelQty(posRemainingStock(product, claimed) > 0 ? 1 : 0);
  };

  const addToOrder = () => {
    if (!selected) return;
    // Re-clamped at the moment of adding rather than trusting panelQty: the
    // stock figure is live, so Inventory may have lowered it while the panel
    // sat open.
    const quantity = Math.min(panelQty, posRemainingStock(selected, claimed));
    if (quantity <= 0) return;
    const { selected: chosen, missing } = resolvePosSelections(selected, panelPicks);
    if (missing.length > 0) return;
    const key = posCartLineKey(selected.id, chosen);
    setCart(prev => ({
      ...prev,
      [key]: { productId: selected.id, picks: panelPicks, quantity: (prev[key]?.quantity || 0) + quantity },
    }));
    setSelected(null);
  };

  /**
   * Steps one order line up or down from the rail.
   *
   * @param {string} key - the line's cart key
   * @param {-1|1} delta
   * @returns {void}
   */
  const changeQty = (key, delta) => setCart((prev) => {
    const line = prev[key];
    if (!line) return prev;
    const next = { ...prev };
    const updated = line.quantity + delta;
    if (updated <= 0) {
      delete next[key];
      return next;
    }
    // The rail's + is a second way to raise a line, so it needs the same
    // ceiling the panel enforces — the product's stock less what its OTHER
    // lines already hold, since every line of it draws on the one shelf.
    const stock = Math.max(0, Math.floor(Number(products.find(p => p.id === line.productId)?.supplies) || 0));
    const otherLines = Object.entries(prev)
      .filter(([otherKey, other]) => otherKey !== key && other.productId === line.productId)
      .reduce((sum, [, other]) => sum + other.quantity, 0);
    next[key] = { ...line, quantity: Math.min(updated, Math.max(0, stock - otherLines)) };
    return next;
  });

  const handleConfirmCharge = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      // VAT is passed rather than looked up inside placePosOrder: this screen
      // already holds the merged config, and the order has to be written with
      // the SAME settings the rail has been quoting to the customer.
      const { id, order } = await placePosOrder(
        clientId,
        cartLines,
        // The live list goes with it, so a method this club added resolves to
        // itself rather than falling through to the built-in names.
        {
          method: paymentMethod,
          methods: payMethods,
          cashReceived: Number(cashReceived),
          deliveryFee: appliedFee,
        },
        staffEmail,
        { vatMode: config?.vatMode, vatRate: config?.vatRate },
      );
      setCart({});
      closeCharging();
      setPlaced({ id, order });
    } catch (error) {
      // Stays on the receipt: nothing was written and nothing was taken from
      // stock, so the staff can fix the quantity and confirm again.
      setModal({ title: 'Could not complete the sale', message: error.message, type: 'error' });
    } finally {
      setConfirming(false);
    }
  };

  if (loading || !sortLoaded) return <div className="pos-loading">{spinnerRing('Loading products…')}</div>;

  return (
    <div className="pos-dashboard">
      <div className="pos-dash-main">
        {/* ---------- Category cards ---------- */}
        <div className="pos-cat-rail" role="tablist">
          <button
            type="button" role="tab"
            aria-selected={activeCategory === POS_ALL_CATEGORIES}
            className={`pos-cat-card${activeCategory === POS_ALL_CATEGORIES ? ' active' : ''}`}
            onClick={() => setActiveCategory(POS_ALL_CATEGORIES)}
          >
            <span className="pos-cat-icon"><LayoutGrid size={18} /></span>
            <span className="pos-cat-name">All products</span>
            <span className="pos-cat-count">{products.length} items</span>
          </button>

          {categories.map((name) => (
            <button
              key={name} type="button" role="tab"
              aria-selected={activeCategory === name}
              className={`pos-cat-card${activeCategory === name ? ' active' : ''}`}
              onClick={() => setActiveCategory(name)}
            >
              <span className="pos-cat-icon">{categoryIcon(name)}</span>
              <span className="pos-cat-name">{name}</span>
              <span className="pos-cat-count">{countsByCategory.get(name) || 0} items</span>
            </button>
          ))}
        </div>

        {/* ---------- Search and sort ---------- */}
        {/* One row on a wide screen, stacked on a narrow one — the sort
            control is a second way of finding a product, so it belongs beside
            the first rather than above the grid on its own line. */}
        <div className="pos-find-row">
          <div className="pos-search-bar">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search for a product…"
              aria-label="Search products"
            />
            <Search size={17} aria-hidden="true" />
          </div>

          <PosSortBar
            sortKey={sortKey}
            direction={sortDir}
            onSortChange={changeSort}
            onDirectionToggle={toggleDirection}
            onSaveCustom={canArrange ? handleSaveCustomSort : undefined}
            customDirty={customSort.dirty}
            customSaving={customSort.saving}
          />
        </div>

        {arranging && (
          <p className="pos-arrange-hint">
            Drag the cards into the order you want (press and hold on a touch screen), then tap Save. The order applies to both Products and Inventory.
          </p>
        )}

        {/* ---------- Product grid ---------- */}
        {visibleProducts.length === 0 ? (
          <p className="pos-empty-line">No products to show.</p>
        ) : (
          <>
          <div
            className={`pos-till-grid ${posCardGridAttrs(config).className}${arranging ? ' pos-grid-arrange' : ''}${arranging && customSort.wiggle ? ' pos-grid-unsaved' : ''}`}
            style={posCardGridAttrs(config).style}
          >
            {pagedProducts.map((product) => {
              const inCart = claimed[product.id] || 0;
              const stock = Math.max(0, Math.floor(Number(product.supplies) || 0));
              const soldOut = stock <= 0;
              // Running low is about what is ON THE SHELF, not what this order
              // has claimed — the person who needs to reorder is asking about
              // the stockroom, and a full cart does not empty it.
              //
              // The count is shown on EVERY tile; this only decides whether it
              // is red. "Out" counts as short too — a zero that looked like any
              // other figure would be the one number on the shelf that most
              // needs to stand out.
              const shortOfStock = posStockLevel(product, config?.lowStockThreshold) !== 'ok';
              // Nothing left to add — either the shelf is empty or this order
              // already holds all of it. Both cases make the card a dead end,
              // so both are greyed out rather than opening a panel whose Add
              // button is disabled.
              const unavailable = posRemainingStock(product, claimed) <= 0;
              const outLabel = soldOut ? 'Out of stock' : 'All in order';
              return (
                <button
                  key={product.id}
                  type="button"
                  className={`pos-till-card${unavailable ? ' out' : ''}${draggingId === product.id ? ' dragging' : ''}`}
                  onClick={() => { if (!unavailable) openProduct(product); }}
                  // Not `disabled` while arranging: a disabled button receives
                  // no pointer events, so a sold-out card could not be dragged.
                  disabled={unavailable && !arranging}
                  aria-disabled={unavailable}
                  {...getDragProps(product.id)}
                  title={unavailable ? `${product.name} — ${outLabel}` : product.name}
                >
                  <span className="pos-till-media">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt="" />
                      : <span className="pos-product-noimage">No image</span>}
                    {/* The stock count, on every tile. Top RIGHT, opposite the
                        cart badge, so the two can never reach each other; the
                        out-of-stock ribbon still runs along the bottom, which
                        keeps "how many" and "none at all" in different places.

                        Always present rather than only when short, so the
                        figure is somewhere to read at a glance — the colour,
                        not the presence, is what carries the warning. */}
                    <span className={`pos-till-low${shortOfStock ? ' low' : ''}`}>
                      {stock} Stocks
                    </span>
                    {/* How many are already in the order, on the image where
                        it is visible without reading the rail. */}
                    {inCart > 0 && <span className="pos-till-badge">{inCart}</span>}
                    {unavailable && <span className="pos-till-out">{outLabel}</span>}
                  </span>
                  <span className="pos-till-name">{product.name}</span>
                  <span className="pos-till-meta">
                    {product.category
                      ? <span className="pos-till-chip">{product.category}</span>
                      : <span />}
                    <span className="pos-till-price">{formatMoney(Number(product.price || 0), config)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* Only when a limit is set — with paging off there is one page and
              PosPager renders nothing anyway, but skipping it outright keeps
              the default screen exactly as it was. */}
          {pageSize > 0 && (
            <PosPager
              page={safePage}
              totalItems={visibleProducts.length}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          )}
          </>
        )}
      </div>

      {/* ---------- Order rail ---------- */}
      {/* Hidden outright without the permission, rather than shown empty and
          refusing at the end: a cart someone can fill and then not pay off is
          a worse discovery at the counter than no cart at all. */}
      {canProcessOrders && (
      <aside className="pos-order-rail">
        <h2>Order Summary</h2>

        {cartLines.length === 0 ? (
          <p className="pos-empty-line">Nothing added yet.</p>
        ) : (
          <>
            <ul className="pos-order-lines">
              {cartLines.map(({ key, product, quantity, options, unitPrice }) => {
                const stock = Math.max(0, Math.floor(Number(product.supplies) || 0));
                // Full when the PRODUCT is used up across all its lines, not
                // just this one.
                const full = (claimed[product.id] || 0) >= stock;
                return (
                <li key={key} className="pos-line">
                  <span className="pos-line-media">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt="" />
                      : <span className="pos-product-noimage">&nbsp;</span>}
                  </span>
                  <span className="pos-line-info">
                    <span className="pos-line-name">{product.name}</span>
                    <PosSelectedOptions options={options} basePrice={Number(product.price) || 0} className="pos-line-opts" />
                    <span className="pos-line-price">
                      {formatMoney(roundMoney(unitPrice * quantity), config)}
                    </span>
                  </span>
                  <span className="pos-line-qty">
                    <button type="button" aria-label={`One less ${product.name}`} onClick={() => changeQty(key, -1)}>
                      <Minus size={12} />
                    </button>
                    <span>{quantity}</span>
                    {/* Stops at the shelf, same ceiling the panel applies. */}
                    <button
                      type="button"
                      aria-label={`One more ${product.name}`}
                      onClick={() => changeQty(key, 1)}
                      disabled={full}
                      title={full ? `Only ${stock} in stock` : undefined}
                    >
                      <Plus size={12} />
                    </button>
                  </span>
                </li>
                );
              })}
            </ul>

            <div className="pos-totals">
              <div className="pos-total-line">
                <span>Subtotal</span>
                <span>{formatMoney(vat.subtotal, config)}</span>
              </div>

              {/* VAT INCLUSIVE — the subtotal is SPLIT, not added to. Both
                  lines are indented under it because neither is a charge:
                  together they say what the subtotal above was made of, and
                  the TOTAL below is unchanged. */}
              {vat.applies && vat.mode === POS_VAT_MODE_INCLUSIVE && (
                <>
                  <div className="pos-total-line pos-total-part">
                    <span>Item cost ({formatAmount(vat.netShare)}%)</span>
                    <span>{formatMoney(vat.net, config)}</span>
                  </div>
                  <div className="pos-total-line pos-total-part">
                    <span>VAT ({formatAmount(vat.rate)}%)</span>
                    <span>{formatMoney(vat.vat, config)}</span>
                  </div>
                </>
              )}

              {/* VAT EXCLUSIVE — a charge in its own right, so it sits flush
                  with the subtotal it is being added to rather than indented
                  under it. The TOTAL below is the two summed. */}
              {vat.applies && vat.mode === POS_VAT_MODE_EXCLUSIVE && (
                <div className="pos-total-line">
                  <span>VAT ({formatAmount(vat.rate)}%)</span>
                  <span>{formatMoney(vat.vat, config)}</span>
                </div>
              )}

              <div className="pos-total-line">
                <span>Items</span>
                <span>{cartLines.reduce((n, l) => n + l.quantity, 0)}</span>
              </div>
              <div className="pos-total-line pos-total-grand">
                <span>TOTAL</span>
                <span>{formatMoney(cartTotal, config)}</span>
              </div>
            </div>

            <button type="button" className="pos-btn pos-btn-primary pos-place-order" onClick={() => setCharging(true)}>
              Place order &middot; {formatMoney(cartTotal, config)}
            </button>
            <button type="button" className="pos-btn pos-btn-ghost" onClick={() => setCart({})}>Clear order</button>
          </>
        )}
      </aside>
      )}

      {/* ---------- Side panel ---------- */}
      {selected && (
        <div className="pos-panel" role="dialog" aria-label={selected.name}>
          <header className="pos-panel-head">
            <h3>{selected.name}</h3>
            <button type="button" onClick={() => setSelected(null)} aria-label="Close"><X size={18} /></button>
          </header>
          <div className="pos-panel-body">
            {selected.imageUrl && <img className="pos-panel-image" src={selected.imageUrl} alt="" />}
            {selected.description && <p className="pos-panel-desc">{selected.description}</p>}
            {/* With options, the price follows the answers — "₱80" becomes
                "₱95" the moment pearls are tapped, before anything is added. */}
            <p className="pos-panel-price">
              {formatMoney(canProcessOrders ? panelUnitPrice : Number(selected.price || 0), config)}
              {canProcessOrders && panelChoice.extra > 0 && (
                <span className="pos-panel-price-base">
                  {formatMoney(Number(selected.price || 0), config)} + {formatMoney(panelChoice.extra, config)} options
                </span>
              )}
            </p>

            {/* The quantity stepper and Add to order exist only to build an
                order, so without the permission to take one they are not
                shown at all — this screen becomes the price-and-stock
                reference it still usefully is. */}
            {canProcessOrders && (
            <>
            {posHasCustomizations(selected) && (
              <PosCustomizationPicker
                product={selected}
                picks={panelPicks}
                onChange={setPanelPicks}
                config={config}
                disabled={panelMax <= 0}
              />
            )}

            {/* Capped at what is actually on the shelf. The customer finding
                out at the counter that the till over-promised is worse than
                the stepper simply refusing to go higher. */}
            <label className="pos-field">
              <span>Quantity to add</span>
              <div className="pos-stepper">
                <button
                  type="button"
                  onClick={() => setPanelQty(q => clampPanelQty(q - 1))}
                  disabled={panelQty <= 1}
                  aria-label="Fewer"
                >
                  <Minus size={14} />
                </button>
                <input
                  type="number"
                  min="1"
                  max={panelMax}
                  value={panelQty}
                  onChange={(e) => setPanelQty(clampPanelQty(e.target.value))}
                  disabled={panelMax <= 0}
                />
                <button
                  type="button"
                  onClick={() => setPanelQty(q => clampPanelQty(q + 1))}
                  disabled={panelQty >= panelMax}
                  aria-label="More"
                >
                  <Plus size={14} />
                </button>
              </div>
            </label>

            {/* Says why the stepper stopped, so a capped field does not read
                as a broken one. */}
            {panelMax <= 0 ? (
              <p className="pos-panel-cap out">
                {(Number(selected.supplies) || 0) <= 0
                  ? 'Out of stock — nothing to add.'
                  : `All ${panelInCart} in stock are already in this order.`}
              </p>
            ) : panelInCart > 0 && (
              <p className="pos-panel-cap">
                {panelInCart} already in this order &middot; {panelMax} left to add.
              </p>
            )}

            {/* Says what is still unanswered, so a greyed Add button is never
                a puzzle. */}
            {panelMax > 0 && panelChoice.missing.length > 0 && (
              <p className="pos-panel-cap out">Still to choose: {panelChoice.missing.join(', ')}.</p>
            )}

            <button
              type="button"
              className="pos-btn pos-btn-primary"
              onClick={addToOrder}
              disabled={panelMax <= 0 || panelQty <= 0 || panelChoice.missing.length > 0}
            >
              Add to order
              {panelMax > 0 && panelQty > 0 && posHasCustomizations(selected)
                ? ` · ${formatMoney(roundMoney(panelUnitPrice * panelQty), config)}`
                : ''}
            </button>
            </>
            )}

            <hr className="pos-panel-rule" />

            {/* Read-only here on purpose. The till is for selling; stock is
                corrected in Inventory, where the change sits beside the rest
                of the product's details and goes through one save path. Two
                places to edit the same number is how the two end up
                disagreeing. */}
            <p className="pos-panel-stock">
              <span>Available stocks</span>
              <strong className={(Number(selected.supplies) || 0) <= 0 ? 'out' : undefined}>
                {Number(selected.supplies) || 0}
              </strong>
            </p>
            <p className="pos-panel-hint">Edit this in the Inventory tab.</p>
          </div>
        </div>
      )}

      {/* ---------- Confirmation ---------- */}
      {charging && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Order confirmation">
            <header className="pos-modal-head">
              <h3>Order Confirmation</h3>
              <button type="button" className="pos-modal-close" onClick={closeCharging} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              <PosReceipt
                order={{
                  ...draftOrder,
                  paymentMethod,
                  cashReceived: Number(cashReceived) || null,
                  change: cashChange,
                }}
                courtName={config?.courtName}
                showPaymentNote
                beforeNote={(
                  <PosDeliveryFeeField
                    checked={withDelivery}
                    onCheckedChange={(checked) => { setWithDelivery(checked); if (!checked) setDeliveryFee(''); }}
                    fee={deliveryFee}
                    onFeeChange={setDeliveryFee}
                    disabled={confirming}
                  />
                )}
              />

              {/* Picked from a row of boxes rather than inferred from whether
                  the cash field was filled in: the old screen could not tell
                  GCash from Maya from a bank transfer, so a client's takings
                  were only ever "cash" and "not cash". */}
              <fieldset className="pos-paymethods">
                <legend>Mode of payment</legend>
                <div className="pos-paymethod-grid">
                  {payMethods.map(({ key, label, icon }) => (
                    <button
                      key={key}
                      type="button"
                      className={`pos-paymethod${paymentMethod === key ? ' active' : ''}`}
                      onClick={() => choosePaymentMethod(key)}
                      aria-pressed={paymentMethod === key}
                      disabled={confirming}
                    >
                      {/* An Iconify id is drawn as a CSS mask rather than an
                          <img>, so it takes currentColor and inverts with the
                          active button — and nothing fetched from a third
                          party is injected into this app as markup. Same
                          treatment the category icons get. */}
                      <span className="pos-paymethod-icon" aria-hidden="true">
                        {icon && posIconIsIconify(icon) ? (
                          <span
                            className="pos-icon-mask"
                            style={{ WebkitMaskImage: `url(${posIconifyUrl(icon)})`, maskImage: `url(${posIconifyUrl(icon)})` }}
                          />
                        ) : icon}
                      </span>
                      <span className="pos-paymethod-label">{label}</span>
                    </button>
                  ))}
                </div>
              </fieldset>

              {/* Cash is the only method that tenders an amount and gives
                  change, so the field only exists for it. */}
              {payingCash && (
                <label className="pos-field pos-cash-field">
                  <span>Cash received</span>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={cashReceived}
                    onChange={(e) => setCashReceived(e.target.value)}
                    placeholder={String(chargeTotal)}
                    inputMode="numeric"
                    disabled={confirming}
                  />
                </label>
              )}

              {/* Warned, not blocked: staff may be recording a part payment,
                  and the till should not argue with the person holding the
                  money. */}
              {payingCash && cashReceived !== '' && Number(cashReceived) < chargeTotal && (
                <p className="pos-cash-warn">
                  That is less than the total &mdash; short by {formatMoney((chargeTotal - Number(cashReceived)), config)}.
                </p>
              )}
            </div>
            <footer className="pos-modal-foot">
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-ghost" onClick={closeCharging} disabled={confirming}>Cancel</button>
              <button type="button" className="pos-btn pos-btn-primary" onClick={handleConfirmCharge} disabled={confirming || !feeValid}>
                {confirming ? 'Recording…' : 'Confirm'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* Recorded — the receipt itself, not a line of text about it. */}
      {placed && (
        <div className="pos-modal-overlay">
          <div className="pos-modal pos-modal-receipt" role="dialog" aria-modal="true" aria-label="Order receipt">
            <header className="pos-modal-head">
              <h3>Order recorded</h3>
              <button type="button" className="pos-modal-close" onClick={() => setPlaced(null)} aria-label="Close"><X size={18} /></button>
            </header>
            <div className="pos-modal-body">
              <PosReceipt order={placed.order} courtName={config?.courtName} reference={placed.id} />
            </div>
            <footer className="pos-modal-foot">
              <span className="pos-foot-spacer" />
              <button type="button" className="pos-btn pos-btn-primary" onClick={() => setPlaced(null)}>Done</button>
            </footer>
          </div>
        </div>
      )}

      <Modal
        isOpen={!!modal}
        title={modal?.title || ''}
        message={modal?.message || ''}
        type={modal?.type || 'info'}
        confirmText="OK"
        onClose={() => setModal(null)}
      />
    </div>
  );
}

export default PosDashboard;
